import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Building2,
  Megaphone,
  PhoneCall,
  MapPin,
  BookOpen,
  ShieldCheck,
} from 'lucide-react'
import {
  Container,
  SectionLabel,
  SectionHeading,
  FeatureCard,
  CtaBand,
  CheckList,
  PrimaryButton,
  SecondaryButton,
  ease,
} from '@/components/umbrella/ui'
import UsMapGraphic from '@/components/umbrella/UsMapGraphic'
import { NASAA_SITE_URL } from '@/lib/site'

export const Route = createFileRoute('/solutions/state-agencies')({
  component: StateAgenciesPage,
})

function StateAgenciesHero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-100">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_80%_20%,rgba(37,99,235,0.07),transparent_60%)]" />
      <Container className="grid items-center gap-12 py-20 lg:grid-cols-2 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
        >
          <SectionLabel>For State Agencies</SectionLabel>
          <h1 className="mt-4 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
            Help residents recognize scams{' '}
            <span className="text-blue-600">before it costs them.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
            Attorneys general, securities regulators, treasury offices, and aging
            departments all field the same calls after the money is gone.
            ScamDefense gives your agency a ready-made, state-branded education
            platform that helps residents protect themselves first.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <PrimaryButton label="See the live experience" href={NASAA_SITE_URL} external />
            <SecondaryButton label="Talk to our team" href="#contact" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease, delay: 0.1 }}
        >
          <UsMapGraphic />
        </motion.div>
      </Container>
    </section>
  )
}

const FEATURES = [
  {
    Icon: Building2,
    title: 'State-branded education hub',
    body: 'A public scam-prevention site in your agency’s identity, ready for residents to use without creating an account.',
  },
  {
    Icon: Megaphone,
    title: 'Awareness campaign assets',
    body: 'Social posts, printable handouts, and shareable guides you can put behind a press release or outreach event.',
  },
  {
    Icon: PhoneCall,
    title: 'Reporting routes to your hotlines',
    body: 'Every "what do I do now" path points residents to your consumer protection line and official reporting channels.',
  },
  {
    Icon: MapPin,
    title: 'Local scam-trend data',
    body: 'Surface the fraud types hitting your state, grounded in FTC and FBI reporting, so guidance feels relevant and current.',
  },
  {
    Icon: BookOpen,
    title: 'Plain-language courses',
    body: 'Short, practical lessons written for real residents, not security professionals, and tested for clarity.',
  },
  {
    Icon: ShieldCheck,
    title: 'Privacy-first public access',
    body: 'No logins, no data harvesting. Residents get help, and you get a tool that respects them.',
  },
]

function StateAgenciesPage() {
  return (
    <div className="bg-white">
      <StateAgenciesHero />

      <Container className="py-20">
        <SectionHeading
          label="What you get"
          title="A scam-prevention program, ready to deploy"
          blurb="Everything residents need to recognize, avoid, and report scams, in your agency’s name."
          wide
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(f => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </Container>

      <section className="bg-slate-50 py-20">
        <Container className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              label="Tailored to your state"
              title="Your brand, your hotlines, your residents"
              align="left"
            />
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              We configure the experience around your agency so it reads as an
              official resource, not a third-party product.
            </p>
          </div>
          <CheckList
            items={[
              'Your seal, colors, and agency name throughout',
              'Reporting and help routed to your consumer protection line',
              'Scam trends and examples relevant to your state',
              'Hosted on your own subdomain or linked from your site',
              'Continuously updated as new scams emerge',
              'Optional engagement reporting, with privacy built in',
            ]}
          />
        </Container>
      </section>

      <CtaBand
        title="Give your residents a head start on fraud."
        blurb="Let’s talk about a state-branded ScamDefense deployment for your agency."
      />
    </div>
  )
}
